import {service, factories, models, Report} from 'powerbi-client';
import type {IReportEmbedConfiguration} from 'powerbi-client';
import {SphereReport} from '@/stores/apolloPlatform/cores/portalNavigation';
import {getColourblindPalette} from '@/components/shared/charts/ChartRender/colours';

// The `powerbi-client` doesn’t export a detailed `IReportTheme` or `ICustomTheme` interface.
import light from '@/components/pages/ReportView/theme/light.json';
import dark from '@/components/pages/ReportView/theme/dark.json';
import oldLight from '@/components/pages/ReportView/theme/old/light.json';
import oldDark from '@/components/pages/ReportView/theme/old/dark.json';
// https://ekco.atlassian.net/wiki/x/AQDoiw (Ben's V3)
import newLight from '@/components/pages/ReportView/theme/new/light.json';
import newDark from '@/components/pages/ReportView/theme/new/dark.json';

const embedConfiguration: IReportEmbedConfiguration = {
  type: 'report',
  id: '5dac7a4a-4452-46b3-99f6-a25915e0fe55',
  embedUrl: 'https://app.powerbi.com/reportEmbed',
  tokenType: models.TokenType.Embed,
  accessToken: 'e4...rf',
  settings: {
    background: models.BackgroundType.Transparent,
    navContentPaneEnabled: false,
  },
};

export const applyTheme = async (embed: Report, forceLightTheme = false) => {
  const userSettingsStore = useUserSettingsStore();
  const {theme: userTheme, colorblind} = userSettingsStore.userSettings;
  const isLight = forceLightTheme || userTheme === 'light-only';
  // @ts-expect-error The data team uses custom JSON themes.
  const reportTheme: models.ICustomTheme = await embed.getTheme();
  const name: string = reportTheme.themeJson.name;
  /*
   * N.B. When working with themes, you might see style attributes on elements in the inspector.
   * These styles are added directly to elements in PowerBI Desktop by the data team. They can't be overwritten by styles from the theme JSON.
   */
  let themeJson: models.ICustomTheme['themeJson'] = isLight ? newLight : newDark;
  if (name.includes('EkcoSphere Theme')) themeJson = isLight ? light : dark;
  if (name.includes('ITON Theme')) themeJson = isLight ? oldLight : oldDark; // Older reports need the entire old theme.
  // Colourblind users get the same palette as the Chart.js charts, on top of whichever theme applies.
  if (colorblind) {
    const dataColors = getColourblindPalette(!isLight);
    themeJson = {...themeJson, dataColors, tableAccent: dataColors[4]};
  }
  const customTheme: models.ICustomTheme = {themeJson};
  await embed.applyTheme(customTheme);
};

export const embedReport = (id: string, report: SphereReport, isMobile: boolean) => {
  const el = document.getElementById(id);
  if (el === null) throw Error('Report element not found.');
  const pbiService = new service.Service(
    factories.hpmFactory,
    factories.wpmpFactory,
    factories.routerFactory,
  );
  /**
   * N.B. embed.switchLayout() does not work for mobile. Instead, we have to re-embed entirely.
   * @see {@link https://learn.microsoft.com/en-us/javascript/api/overview/powerbi/update-settings#considerations-and-limitations}
   */
  pbiService.reset(el);
  const userSettingsStore = useUserSettingsStore();
  /**
   * If the component has already been created and attached to the element, reuses the component instance and existing iframe, otherwise creates a new component instance.
   * @see {@link apollo_portal/node_modules/powerbi-client/dist/powerbi-client.d.ts} > embed
   * N.B. No need to bootstrap as we fetch essential info in PageView.vue before mount.
   */
  const embed = pbiService.embed(el, {
    ...embedConfiguration,
    id: report.report_id,
    embedUrl: report.token.embedUrl,
    accessToken: report.token.accessToken,
    settings: {
      ...embedConfiguration.settings,
      layoutType: isMobile ? models.LayoutType.MobilePortrait : models.LayoutType.Custom,
      customLayout: {displayOption: models.DisplayOption.FitToPage},
      localeSettings: {
        language: userSettingsStore.userSettings.language,
        formatLocale: userSettingsStore.userSettings.language,
      },
    },
  }) as Report;
  return embed;
};
